
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  RefreshCw,
  MapPin,
  Phone,
  Mail,
  Clock,
  Globe,
  Pencil,
  Trash2,
  Eye,
  X,
  Upload,
  Building2,
  Users,
  CalendarDays,
  ExternalLink,
  Image as ImageIcon,
  LoaderCircle,
  CheckCircle2,
  AlertCircle,
  Code2,
  Palette,
  ShieldCheck,
  ChevronDown,
} from "lucide-react";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL ||
  "https://pmswebbackend.pixelmindsolutions.com"
).replace(/\/+$/, "");

const API_URL = `${API_BASE_URL}/api/branches`;

const EMPTY_FORM = {
  id: "",
  city: "",
  badge: "",
  title: "",
  address: "",
  phone: "",
  email: "",
  hours: "",
  mapLink: "",
  teamSize: "",
  established: "",
  features: [],
};

const DEFAULT_FEATURES = [
  { icon: "bi bi-code-slash", text: "Main Development Center" },
  { icon: "bi bi-palette", text: "UI/UX Design Studio" },
  { icon: "bi bi-shield-check", text: "Quality Assurance Lab" },
  { icon: "bi bi-people", text: "Client Collaboration Hub" },
];

const FEATURE_ICONS = [
  { label: "Code", value: "bi bi-code-slash" },
  { label: "Design", value: "bi bi-palette" },
  { label: "Security", value: "bi bi-shield-check" },
  { label: "Team", value: "bi bi-people" },
];

function getImageUrl(image) {
  if (!image) return "";

  if (
    image.startsWith("http://") ||
    image.startsWith("https://") ||
    image.startsWith("data:")
  ) {
    return image;
  }

  return `${API_BASE_URL}${image.startsWith("/") ? "" : "/"}${image}`;
}

function getErrorMessage(data, fallback) {
  return data?.message || fallback;
}

function FeatureIcon({ icon, size = 18 }) {
  if (icon?.includes("palette")) {
    return <Palette size={size} />;
  }

  if (icon?.includes("shield")) {
    return <ShieldCheck size={size} />;
  }

  if (icon?.includes("people")) {
    return <Users size={size} />;
  }

  return <Code2 size={size} />;
}

function Toast({ toast, onClose }) {
  if (!toast) return null;

  const isSuccess = toast.type === "success";

  return (
    <div
      role="status"
      className={`fixed right-4 top-4 z-[100] flex max-w-[calc(100vw-2rem)] items-start gap-3 rounded-xl border bg-white p-4 shadow-xl sm:right-6 sm:top-6 ${
        isSuccess ? "border-emerald-200" : "border-red-200"
      }`}
    >
      {isSuccess ? (
        <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={20} />
      ) : (
        <AlertCircle className="mt-0.5 shrink-0 text-red-600" size={20} />
      )}

      <p className="text-sm font-medium text-slate-700">{toast.message}</p>

      <button
        type="button"
        onClick={onClose}
        className="ml-2 rounded-lg p-1 text-slate-400 hover:bg-slate-100"
        aria-label="Close notification"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function FormField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-100";

export default function BranchManagement() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState("");
  const [toast, setToast] = useState(null);

  const notify = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const fetchBranches = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);

    try {
      const response = await fetch(API_URL);
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getErrorMessage(result, "Failed to fetch branches")
        );
      }

      setBranches(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      notify(error.message || "Unable to load branches", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    if (!toast) return undefined;

    const timeout = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    return () => {
      if (imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const filteredBranches = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return branches;

    return branches.filter((branch) =>
      [
        branch.city,
        branch.title,
        branch.badge,
        branch.address,
        branch.email,
        branch.phone,
      ].some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [branches, search]);

  const closeModal = () => {
    setModal(null);
    setForm(EMPTY_FORM);
    setEditingId("");
    setSelectedImage(null);
    setImagePreview("");
    setExistingImage("");
  };

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      features: DEFAULT_FEATURES.map((feature) => ({ ...feature })),
    });

    setEditingId("");
    setSelectedImage(null);
    setImagePreview("");
    setExistingImage("");
    setModal("create");
  };

  const openEdit = (branch) => {
    setEditingId(branch._id);

    setForm({
      id: branch.id ?? "",
      city: branch.city || "",
      badge: branch.badge || "",
      title: branch.title || "",
      address: branch.address || "",
      phone: branch.phone || "",
      email: branch.email || "",
      hours: branch.hours || "",
      mapLink: branch.mapLink || "",
      teamSize: branch.teamSize || "",
      established: branch.established || "",
      features: Array.isArray(branch.features)
        ? branch.features.map((feature) => ({
            icon: feature.icon || "bi bi-code-slash",
            text: feature.text || "",
          }))
        : [],
    });

    setExistingImage(branch.image || "");
    setSelectedImage(null);
    setImagePreview("");
    setModal("edit");
  };

  const openView = (branch) => {
    setModal({ type: "view", branch });
  };

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
    ];

    if (!allowedTypes.includes(file.type)) {
      notify("Choose a JPG, PNG, WEBP, or GIF image.", "error");
      event.target.value = "";
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const addFeature = () => {
    setForm((previous) => ({
      ...previous,
      features: [
        ...previous.features,
        { icon: "bi bi-code-slash", text: "" },
      ],
    }));
  };

  const updateFeature = (index, key, value) => {
    setForm((previous) => ({
      ...previous,
      features: previous.features.map((feature, featureIndex) =>
        featureIndex === index
          ? { ...feature, [key]: value }
          : feature
      ),
    }));
  };

  const removeFeature = (index) => {
    setForm((previous) => ({
      ...previous,
      features: previous.features.filter(
        (_, featureIndex) => featureIndex !== index
      ),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.city.trim() || !form.title.trim() || !form.address.trim()) {
      notify("City, branch title, and address are required.", "error");
      return;
    }

    if (!form.phone.trim() || !form.email.trim()) {
      notify("Phone number and email are required.", "error");
      return;
    }

    if (
      form.features.some(
        (feature) => !feature.text || !feature.text.trim()
      )
    ) {
      notify("Please enter text for every branch feature.", "error");
      return;
    }

    const payload = new FormData();

    Object.entries(form).forEach(([key, value]) => {
      if (key === "features") {
        payload.append("features", JSON.stringify(value));
      } else if (value !== null && value !== undefined && value !== "") {
        payload.append(key, String(value));
      }
    });

    if (selectedImage) {
      payload.append("image", selectedImage);
    }

    const isEditing = modal === "edit";
    const endpoint = isEditing
      ? `${API_URL}/${editingId}`
      : API_URL;

    setSaving(true);

    try {
      const response = await fetch(endpoint, {
        method: isEditing ? "PUT" : "POST",
        body: payload,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getErrorMessage(
            result,
            `Failed to ${isEditing ? "update" : "create"} branch`
          )
        );
      }

      notify(
        result.message ||
          `Branch ${isEditing ? "updated" : "created"} successfully`
      );

      closeModal();
      await fetchBranches(false);
    } catch (error) {
      notify(error.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (branch) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${branch.title}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeletingId(branch._id);

    try {
      const response = await fetch(`${API_URL}/${branch._id}`, {
        method: "DELETE",
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getErrorMessage(result, "Failed to delete branch")
        );
      }

      setBranches((previous) =>
        previous.filter((item) => item._id !== branch._id)
      );

      notify(result.message || "Branch deleted successfully");
    } catch (error) {
      notify(error.message || "Failed to delete branch", "error");
    } finally {
      setDeletingId("");
    }
  };

  const stats = useMemo(
    () => [
      {
        label: "Total Branches",
        value: branches.length,
        icon: Building2,
        color: "bg-orange-50 text-orange-600",
      },
      {
        label: "Cities Covered",
        value: new Set(
          branches
            .map((branch) => branch.city?.trim().toLowerCase())
            .filter(Boolean)
        ).size,
        icon: MapPin,
        color: "bg-blue-50 text-blue-600",
      },
      {
        label: "With Office Images",
        value: branches.filter((branch) => branch.image).length,
        icon: ImageIcon,
        color: "bg-purple-50 text-purple-600",
      },
    ],
    [branches]
  );

  const renderBranchActions = (branch) => (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        title="View branch"
        onClick={() => openView(branch)}
        className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
      >
        <Eye size={17} />
      </button>

      <button
        type="button"
        title="Edit branch"
        onClick={() => openEdit(branch)}
        className="rounded-lg p-2 text-slate-500 hover:bg-orange-50 hover:text-orange-600"
      >
        <Pencil size={17} />
      </button>

      <button
        type="button"
        title="Delete branch"
        disabled={deletingId === branch._id}
        onClick={() => handleDelete(branch)}
        className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
      >
        {deletingId === branch._id ? (
          <LoaderCircle size={17} className="animate-spin" />
        ) : (
          <Trash2 size={17} />
        )}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 lg:p-8">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="mx-auto max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm text-slate-500">
              <Building2 size={16} />
              <span>Website Management</span>
              <span>/</span>
              <span className="font-medium text-orange-600">Branches</span>
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Branch Management
            </h1>

            <p className="mt-1 text-sm text-slate-500 sm:text-base">
              Manage office locations, contact information, and branch features.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fetchBranches()}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-50"
            >
              <RefreshCw
                size={17}
                className={loading ? "animate-spin" : ""}
              />
              Refresh
            </button>

            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600"
            >
              <Plus size={18} />
              Add Branch
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {stats.map((stat) => {
            const Icon = stat.icon;

            return (
              <div
                key={stat.label}
                className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      {stat.label}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900">
                      {stat.value}
                    </p>
                  </div>

                  <div className={`rounded-xl p-3 ${stat.color}`}>
                    <Icon size={23} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search
              size={18}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search city, branch, email, phone..."
              className={`${inputClass} pl-10`}
            />
          </div>

          <p className="text-sm text-slate-500">
            Showing{" "}
            <span className="font-semibold text-slate-800">
              {filteredBranches.length}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-slate-800">
              {branches.length}
            </span>{" "}
            branches
          </p>
        </div>

        {/* Branches */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h2 className="font-bold text-slate-900">All Branches</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Office locations and contact details
              </p>
            </div>
            <Building2 size={21} className="text-orange-500" />
          </div>

          {loading ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 p-8">
              <LoaderCircle
                size={34}
                className="animate-spin text-orange-500"
              />
              <p className="text-sm text-slate-500">Loading branches...</p>
            </div>
          ) : filteredBranches.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center px-5 py-12 text-center">
              <div className="rounded-2xl bg-orange-50 p-4 text-orange-500">
                <Building2 size={32} />
              </div>

              <h3 className="mt-4 font-semibold text-slate-900">
                {search ? "No matching branches" : "No branches yet"}
              </h3>

              <p className="mt-1 max-w-sm text-sm text-slate-500">
                {search
                  ? "Try a different search term."
                  : "Add your first office location to get started."}
              </p>

              {!search && (
                <button
                  type="button"
                  onClick={openCreate}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
                >
                  <Plus size={17} />
                  Add First Branch
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop/tablet table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[850px] text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-4 font-semibold">Branch</th>
                      <th className="px-5 py-4 font-semibold">Contact</th>
                      <th className="px-5 py-4 font-semibold">Working Hours</th>
                      <th className="px-5 py-4 font-semibold">Features</th>
                      <th className="px-5 py-4 text-right font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {filteredBranches.map((branch) => (
                      <tr
                        key={branch._id}
                        className="transition hover:bg-slate-50/80"
                      >
                        <td className="px-5 py-4">
                          <div className="flex min-w-56 items-center gap-3">
                            <div className="h-14 w-16 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                              {branch.image ? (
                                <img
                                  src={getImageUrl(branch.image)}
                                  alt={branch.title}
                                  className="h-full w-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="flex h-full items-center justify-center text-slate-400">
                                  <Building2 size={22} />
                                </div>
                              )}
                            </div>

                            <div className="min-w-0">
                              <p className="font-semibold text-slate-900">
                                {branch.title}
                              </p>
                              <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                                <MapPin size={13} className="shrink-0" />
                                {branch.city}
                              </p>
                              {branch.badge && (
                                <span className="mt-1 inline-block rounded-md bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-600">
                                  {branch.badge}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <div className="space-y-2 text-sm text-slate-600">
                            <p className="flex items-center gap-2">
                              <Phone
                                size={14}
                                className="shrink-0 text-slate-400"
                              />
                              {branch.phone}
                            </p>
                            <p className="flex items-center gap-2">
                              <Mail
                                size={14}
                                className="shrink-0 text-slate-400"
                              />
                              <span className="max-w-52 truncate">
                                {branch.email}
                              </span>
                            </p>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <p className="flex max-w-48 items-start gap-2 text-sm text-slate-600">
                            <Clock
                              size={15}
                              className="mt-0.5 shrink-0 text-slate-400"
                            />
                            {branch.hours || "Not specified"}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                            {branch.features?.length || 0} features
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex justify-end">
                            {renderBranchActions(branch)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="grid grid-cols-1 gap-4 p-4 md:hidden">
                {filteredBranches.map((branch) => (
                  <article
                    key={branch._id}
                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                  >
                    <div className="flex gap-3 p-3">
                      <div className="h-20 w-24 shrink-0 overflow-hidden rounded-xl bg-slate-100">
                        {branch.image ? (
                          <img
                            src={getImageUrl(branch.image)}
                            alt={branch.title}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-400">
                            <Building2 size={26} />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        {branch.badge && (
                          <span className="mb-1 inline-block rounded-md bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-600">
                            {branch.badge}
                          </span>
                        )}
                        <h3 className="break-words font-bold text-slate-900">
                          {branch.title}
                        </h3>
                        <p className="mt-1 flex items-start gap-1 text-xs text-slate-500">
                          <MapPin size={13} className="mt-0.5 shrink-0" />
                          {branch.city}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2 border-t border-slate-100 px-4 py-3">
                      <p className="flex items-start gap-2 break-all text-sm text-slate-600">
                        <MapPin size={15} className="mt-0.5 shrink-0 text-slate-400" />
                        {branch.address}
                      </p>
                      <p className="flex items-center gap-2 break-all text-sm text-slate-600">
                        <Phone size={14} className="shrink-0 text-slate-400" />
                        {branch.phone}
                      </p>
                      <p className="flex items-center gap-2 break-all text-sm text-slate-600">
                        <Mail size={14} className="shrink-0 text-slate-400" />
                        {branch.email}
                      </p>
                      <p className="flex items-start gap-2 text-sm text-slate-600">
                        <Clock size={14} className="mt-0.5 shrink-0 text-slate-400" />
                        {branch.hours || "Hours not specified"}
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50 px-3 py-2">
                      <span className="text-xs text-slate-500">
                        {branch.features?.length || 0} features
                      </span>
                      {renderBranchActions(branch)}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      {(modal === "create" || modal === "edit") && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 sm:items-center sm:p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !saving) closeModal();
          }}
        >
          <div className="flex max-h-[95dvh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-h-[92dvh] sm:max-w-4xl sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  {modal === "edit" ? "Edit Branch" : "Add New Branch"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Enter the office information below.
                </p>
              </div>

              <button
                type="button"
                disabled={saving}
                onClick={closeModal}
                className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close form"
              >
                <X size={21} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="min-h-0 flex-1 overflow-y-auto"
            >
              <div className="space-y-6 p-5 sm:p-6">
                {/* Basic details */}
                <section>
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-900">
                    <Building2 size={18} className="text-orange-500" />
                    Branch Information
                  </h3>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <FormField label="City *">
                      <input
                        name="city"
                        value={form.city}
                        onChange={updateField}
                        required
                        placeholder="Hyderabad"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Branch Title *">
                      <input
                        name="title"
                        value={form.title}
                        onChange={updateField}
                        required
                        placeholder="Hyderabad Innovation Center"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Badge">
                      <input
                        name="badge"
                        value={form.badge}
                        onChange={updateField}
                        placeholder="Global Headquarters"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Branch ID (optional)">
                      <input
                        type="number"
                        min="1"
                        name="id"
                        value={form.id}
                        onChange={updateField}
                        placeholder="Auto-generated if omitted"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Team Size">
                      <input
                        name="teamSize"
                        value={form.teamSize}
                        onChange={updateField}
                        placeholder="50+ Experts"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Established">
                      <input
                        name="established"
                        value={form.established}
                        onChange={updateField}
                        placeholder="Since 2018"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Full Address *" className="sm:col-span-2 lg:col-span-3">
                      <textarea
                        name="address"
                        value={form.address}
                        onChange={updateField}
                        required
                        rows={3}
                        placeholder="Enter complete office address"
                        className={inputClass}
                      />
                    </FormField>
                  </div>
                </section>

                {/* Contact details */}
                <section className="border-t border-slate-100 pt-5">
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-900">
                    <Phone size={18} className="text-orange-500" />
                    Contact Information
                  </h3>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField label="Phone Number *">
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={updateField}
                        required
                        placeholder="+91 9666317749"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Email Address *">
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={updateField}
                        required
                        placeholder="office@example.com"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Working Hours">
                      <input
                        name="hours"
                        value={form.hours}
                        onChange={updateField}
                        placeholder="Mon - Sat: 9:00 AM - 6:00 PM"
                        className={inputClass}
                      />
                    </FormField>

                    <FormField label="Google Maps Link">
                      <input
                        type="url"
                        name="mapLink"
                        value={form.mapLink}
                        onChange={updateField}
                        placeholder="https://maps.app.goo.gl/..."
                        className={inputClass}
                      />
                    </FormField>
                  </div>
                </section>

                {/* Office image */}
                <section className="border-t border-slate-100 pt-5">
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-slate-900">
                    <ImageIcon size={18} className="text-orange-500" />
                    Office Image
                  </h3>

                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-5 transition hover:border-orange-300 hover:bg-orange-50/40 sm:p-7">
                    {imagePreview || existingImage ? (
                      <img
                        src={
                          imagePreview ||
                          getImageUrl(existingImage)
                        }
                        alt="Branch preview"
                        className="mb-4 h-40 w-full max-w-md rounded-xl object-cover"
                      />
                    ) : (
                      <div className="mb-3 rounded-xl bg-orange-100 p-3 text-orange-600">
                        <Upload size={25} />
                      </div>
                    )}

                    <span className="text-sm font-semibold text-slate-700">
                      {selectedImage
                        ? selectedImage.name
                        : "Click to upload office image"}
                    </span>
                    <span className="mt-1 text-center text-xs text-slate-500">
                      JPG, PNG, WEBP, or GIF
                    </span>

                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>

                  {selectedImage && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview("");
                      }}
                      className="mt-2 text-sm font-medium text-red-600 hover:underline"
                    >
                      Remove selected image
                    </button>
                  )}
                </section>

                {/* Features */}
                <section className="border-t border-slate-100 pt-5">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="flex items-center gap-2 font-semibold text-slate-900">
                      <ShieldCheck size={18} className="text-orange-500" />
                      Branch Features
                    </h3>

                    <button
                      type="button"
                      onClick={addFeature}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-orange-200 px-3 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50"
                    >
                      <Plus size={16} />
                      Add Feature
                    </button>
                  </div>

                  <div className="space-y-3">
                    {form.features.map((feature, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-1 items-end gap-3 rounded-xl border border-slate-200 p-3 sm:grid-cols-[180px_1fr_auto]"
                      >
                        <FormField label="Icon">
                          <div className="relative">
                            <select
                              value={feature.icon}
                              onChange={(event) =>
                                updateFeature(index, "icon", event.target.value)
                              }
                              className={`${inputClass} appearance-none pr-9`}
                            >
                              {FEATURE_ICONS.map((icon) => (
                                <option key={icon.value} value={icon.value}>
                                  {icon.label}
                                </option>
                              ))}
                              {!FEATURE_ICONS.some(
                                (icon) => icon.value === feature.icon
                              ) && (
                                <option value={feature.icon}>
                                  Custom icon
                                </option>
                              )}
                            </select>
                            <ChevronDown
                              size={15}
                              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                          </div>
                        </FormField>

                        <FormField label="Feature Description">
                          <input
                            value={feature.text}
                            onChange={(event) =>
                              updateFeature(index, "text", event.target.value)
                            }
                            placeholder="Main Development Center"
                            className={inputClass}
                          />
                        </FormField>

                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          title="Remove feature"
                          className="flex h-10 items-center justify-center gap-2 rounded-lg px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                          <span className="sm:hidden">Remove feature</span>
                        </button>
                      </div>
                    ))}

                    {form.features.length === 0 && (
                      <p className="rounded-xl border border-dashed border-slate-200 p-5 text-center text-sm text-slate-500">
                        No features added yet.
                      </p>
                    )}
                  </div>
                </section>
              </div>

              {/* Form actions */}
              <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-100 bg-white p-4 sm:flex-row sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {saving ? (
                    <>
                      <LoaderCircle size={17} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={17} />
                      {modal === "edit" ? "Save Changes" : "Create Branch"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branch details modal */}
      {modal?.type === "view" && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 sm:items-center sm:p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeModal();
          }}
        >
          <div className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">
                Branch Details
              </h2>

              <button
                type="button"
                onClick={closeModal}
                className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close details"
              >
                <X size={21} />
              </button>
            </div>

            {(() => {
              const branch = modal.branch;

              return (
                <div className="space-y-5 p-5 sm:p-6">
                  {branch.image ? (
                    <img
                      src={getImageUrl(branch.image)}
                      alt={branch.title}
                      className="h-52 w-full rounded-2xl object-cover sm:h-64"
                    />
                  ) : (
                    <div className="flex h-40 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                      <ImageIcon size={36} />
                    </div>
                  )}

                  <div>
                    {branch.badge && (
                      <span className="inline-block rounded-lg bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600">
                        {branch.badge}
                      </span>
                    )}

                    <h3 className="mt-2 text-2xl font-bold text-slate-900">
                      {branch.title}
                    </h3>

                    <p className="mt-1 flex items-start gap-2 text-sm text-slate-500">
                      <MapPin size={16} className="mt-0.5 shrink-0" />
                      {branch.city} — {branch.address}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {[
                      {
                        label: "Phone",
                        value: branch.phone,
                        icon: Phone,
                      },
                      {
                        label: "Email",
                        value: branch.email,
                        icon: Mail,
                      },
                      {
                        label: "Working Hours",
                        value: branch.hours,
                        icon: Clock,
                      },
                      {
                        label: "Team Size",
                        value: branch.teamSize,
                        icon: Users,
                      },
                      {
                        label: "Established",
                        value: branch.established,
                        icon: CalendarDays,
                      },
                    ].map((item) => {
                      const Icon = item.icon;

                      return (
                        <div
                          key={item.label}
                          className="rounded-xl border border-slate-200 p-3"
                        >
                          <p className="flex items-center gap-2 text-xs font-medium text-slate-500">
                            <Icon size={15} />
                            {item.label}
                          </p>
                          <p className="mt-1 break-words text-sm font-semibold text-slate-800">
                            {item.value || "Not specified"}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  <div>
                    <h4 className="mb-3 font-semibold text-slate-900">
                      Branch Features
                    </h4>

                    {branch.features?.length ? (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {branch.features.map((feature, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 rounded-xl bg-slate-50 p-3"
                          >
                            <div className="rounded-lg bg-orange-100 p-2 text-orange-600">
                              <FeatureIcon icon={feature.icon} size={17} />
                            </div>
                            <span className="text-sm text-slate-700">
                              {feature.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">
                        No features added.
                      </p>
                    )}
                  </div>

                  {branch.mapLink && (
                    <a
                      href={branch.mapLink}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
                    >
                      <Globe size={17} />
                      View on Google Maps
                      <ExternalLink size={15} />
                    </a>
                  )}

                  <div className="flex justify-end border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      onClick={() => openEdit(branch)}
                      className="inline-flex items-center gap-2 rounded-xl border border-orange-200 px-4 py-2.5 text-sm font-semibold text-orange-600 hover:bg-orange-50"
                    >
                      <Pencil size={16} />
                      Edit Branch
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
